/**
 * When to offer "turn on reminders" — shared by the habits list and the Pilot
 * card (NotificationOptIn renders both).
 *
 * The offer appears after the first completed step on this device and, once
 * answered or waved off, stays away for two weeks. Pilot users used to never
 * see it: the counter only moved in My Habits and the prompt only rendered
 * there, so `push_enabled` stayed false and every Pilot reminder was skipped
 * (2026-10-07).
 */

/** Same keys My Habits has always used — existing answers carry over. */
export const COMPLETIONS_KEY = "trybeup_total_completions";
export const OPT_IN_DISMISS_KEY = "trybeup_notif_optin_dismissed";

// Lowered from 3 → 1 (2026-06-08): the person who completed one step and left
// is exactly who needs a reminder tomorrow.
export const PROMPT_THRESHOLD = 1;
export const REASK_DAYS = 14;

/** Fired on `window` after every recorded completion, so surfaces mounted
 *  before the first step (the web "get the app" banner) can react to it. */
export const COMPLETION_EVENT = "trybeup:completion";

/** Count one completed step (habit tick or Pilot Done). Returns the new total. */
export function recordCompletion(): number {
  let next = 0;
  try {
    next = parseInt(localStorage.getItem(COMPLETIONS_KEY) || "0", 10) + 1;
    localStorage.setItem(COMPLETIONS_KEY, String(next));
  } catch {
    return 0;
  }
  try { window.dispatchEvent(new Event(COMPLETION_EVENT)); } catch { /* no window (tests) */ }
  return next;
}

/** Pure: is the offer due, given what this device remembers? */
export function optInDue(opts: { completions: number; dismissedAt: string | null; now?: number }): boolean {
  const now = opts.now ?? Date.now();
  if (opts.dismissedAt) {
    const at = Date.parse(opts.dismissedAt);
    if (!Number.isNaN(at) && (now - at) / 86_400_000 < REASK_DAYS) return false;
  }
  return opts.completions >= PROMPT_THRESHOLD;
}

export function readOptInState(): { completions: number; dismissedAt: string | null } {
  try {
    return {
      completions: parseInt(localStorage.getItem(COMPLETIONS_KEY) || "0", 10) || 0,
      dismissedAt: localStorage.getItem(OPT_IN_DISMISS_KEY),
    };
  } catch {
    return { completions: 0, dismissedAt: null };
  }
}

/** Remember an answer (yes or not now) so the offer rests for two weeks. */
export function markOptInAnswered(): void {
  try { localStorage.setItem(OPT_IN_DISMISS_KEY, new Date().toISOString()); } catch { /* ignore */ }
}
